import { Controller } from '@hotwired/stimulus';

// Slash blocks mirror the real Sorcerer editor (src/lib/editor/slash).
const BLOCKS = [
  {
    k: '`',
    key: 'code',
    q: 'code',
    render:
      '<div class="line" style="background:var(--bg);border:2px solid var(--border);padding:8px 11px;font-size:12.5px"><span style="color:var(--accent)">function</span> cast(spell) {&nbsp;…&nbsp;}</div>',
  },
  {
    k: '#',
    key: 'heading',
    q: 'heading',
    render:
      '<div class="line" style="font-size:19px;font-weight:700;color:var(--fg)">The Emerald Tablet</div>',
  },
  {
    k: '>',
    key: 'quote',
    q: 'quote',
    render:
      '<div class="line" style="border-left:3px solid var(--accent);padding-left:12px;font-style:italic;color:var(--lp-muted)">As above, so below.</div>',
  },
  {
    k: '[x]',
    key: 'task list',
    q: 'task',
    render:
      '<div class="line">- [x] separate the earth from the fire<br/>- [ ] the subtle from the gross</div>',
  },
  {
    k: '|',
    key: 'table',
    q: 'table',
    render:
      '<div class="line" style="font-size:12.5px"><span style="border:2px solid var(--border);padding:2px 8px">Element</span> <span style="border:2px solid var(--border);padding:2px 8px">Sign</span></div>',
  },
  {
    k: '!',
    key: 'callout',
    q: 'callout',
    render:
      '<div class="line" style="border:2px solid var(--accent);padding:8px 11px;color:var(--fg-2)"><b style="color:var(--accent)">Note</b> — three hyphens make a divider.</div>',
  },
  {
    k: '—',
    key: 'divider',
    q: 'divider',
    render:
      '<div class="line" style="border-top:2px solid var(--border);margin:6px 0"></div>',
  },
];

const KEY = 'sorcerer.mage';

export default class extends Controller {
  static targets = ['sprite', 'stage', 'menu', 'query', 'insert', 'header'];

  connect() {
    this.sel = 0;
    this.buildMenu();
    this.choose(0);
    this.onKey = this.handleKey.bind(this);
    document.addEventListener('keydown', this.onKey);

    // Sticky-header height varies (nav wraps on some widths), so bind the
    // in-page-anchor offset to the measured header instead of a CSS guess.
    this.syncScrollPad = this.syncScrollPad.bind(this);
    this.syncScrollPad();
    window.addEventListener('resize', this.syncScrollPad);

    let saved = 'gray';
    try {
      saved = localStorage.getItem(KEY) || 'gray';
    } catch {
      /* private mode */
    }
    this.applyMage(saved);
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.syncScrollPad);
    if (this.onMenuPointer && this.hasMenuTarget) {
      this.menuTarget.removeEventListener('mouseover', this.onMenuPointer);
      this.menuTarget.removeEventListener('click', this.onMenuPointer);
    }
  }

  syncScrollPad() {
    const pad = `${(this.hasHeaderTarget ? this.headerTarget.offsetHeight : 0) + 16}px`;
    document.documentElement.style.setProperty('--lp-scroll-pad', pad);
    document.documentElement.style.scrollPaddingTop = pad;
  }

  // ── mage picker ──
  pickMage(event) {
    this.applyMage(event.currentTarget.dataset.mage);
  }

  applyMage(mage) {
    document.documentElement.dataset.mage = mage;
    if (this.hasSpriteTarget) this.spriteTarget.src = `/mages/${mage}.gif`;
    if (this.hasStageTarget) this.stageTarget.dataset.name = `${mage} mage`;
    try {
      localStorage.setItem(KEY, mage);
    } catch {
      /* private mode */
    }
    this.element
      .querySelectorAll('[data-mage]')
      .forEach((b) =>
        b.setAttribute('aria-pressed', String(b.dataset.mage === mage)),
      );
  }

  // ── slash demo ──
  buildMenu() {
    this.menuTarget.querySelectorAll('button').forEach((b) => b.remove());

    BLOCKS.forEach((b, i) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.setAttribute('role', 'option');
      el.dataset.blockIndex = String(i);
      el.innerHTML = `<span class="ico">${b.k}</span><span>${b.key[0].toUpperCase()}${b.key.slice(1)}</span><span class="k">/${b.q}</span>`;
      this.menuTarget.appendChild(el);
    });

    this.onMenuPointer = this.onMenuPointer.bind(this);
    this.menuTarget.addEventListener('mouseover', this.onMenuPointer);
    this.menuTarget.addEventListener('click', this.onMenuPointer);
    this.opts = [...this.menuTarget.querySelectorAll('button')];
  }

  onMenuPointer(event) {
    const btn = event.target.closest('button[role="option"]');
    if (!btn || !this.menuTarget.contains(btn)) return;

    const i = Number(btn.dataset.blockIndex);
    if (event.type === 'mouseover') {
      if (btn === this._hoverBtn) return;
      this._hoverBtn = btn;
      this.sel = i;
      this.paint();
    } else if (event.type === 'click') {
      this.choose(i);
    }
  }

  paint() {
    this.opts.forEach((o, i) =>
      o.setAttribute('aria-selected', String(i === this.sel)),
    );
    this.queryTarget.textContent = BLOCKS[this.sel].q;
  }

  choose(i) {
    this.sel = i;
    this.paint();
    this.insertTarget.innerHTML = BLOCKS[i].render;
  }

  handleKey(e) {
    const rect = this.menuTarget.getBoundingClientRect();
    if (!rect.height || rect.top > innerHeight || rect.bottom < 0) return;
    if (e.key === 'ArrowDown') {
      this.sel = (this.sel + 1) % BLOCKS.length;
      this.paint();
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      this.sel = (this.sel - 1 + BLOCKS.length) % BLOCKS.length;
      this.paint();
      e.preventDefault();
    } else if (e.key === 'Enter') {
      this.choose(this.sel);
      e.preventDefault();
    }
  }
}
